import { labelFor } from "@/lib/categories";
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { IMAGES } from "@/lib/images";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import LanguageSelect from "@/components/LanguageSelect";
import { PublicImpactMap } from "@/components/PublicImpactMap";
import { readPreviewRegion } from "@/components/PreviewRegionBanner";
import { REGION_PLANS, buildPlans, eurToFcfa, annualSecondarySavings, regionForCountry } from "@/lib/plans";
import { formatMoneyInt } from "@/lib/money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Check, ArrowRight, PackageSearch, Star, BadgeCheck, Send, Search, Trophy, Crown, Sparkles, MapPin, PackageCheck, Store, Info } from "lucide-react";
import { AvatarImg } from "@/components/AvatarImg";
import { FindoSpinner } from "@/components/FindoSpinner";


export default function Landing() {
  const { t, lang } = useLang();
  const { partner } = useAuth();
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [region, setRegion] = useState(() => readPreviewRegion() || "europe");
  const regionManuallySet = useRef(false);
  const [billing, setBilling] = useState("monthly");
  const plans = buildPlans(region, lang);
  const [quoteSegment, setQuoteSegment] = useState(null);
  const [feed, setFeed] = useState(null);
  const [physInfo, setPhysInfo] = useState(false);
  const [homeEmbed, setHomeEmbed] = useState("https://www.youtube.com/embed/9aEPoI8alJ8?rel=0");

  // Presentation video URL is superadmin-editable (see Superadmin › Accueil).
  useEffect(() => {
    api("/config/home-video", { auth: false })
      .then((r) => { if (r?.embed_url) setHomeEmbed(r.embed_url); })
      .catch(() => {});
  }, []);

  // Auto-detect the visitor's region (Africa → CFA, else Europe) on load.
  // Never override a region the visitor picked manually via the toggle,
  // nor a superadmin "preview as" region override.
  useEffect(() => {
    if (readPreviewRegion()) return;
    api("/geo/my-country", { auth: false })
      .then((r) => {
        if (!regionManuallySet.current && r?.country) {
          setRegion(regionForCountry(r.country));
        }
      })
      .catch(() => {});
  }, []);

  // Live-apply the superadmin preview region override.
  useEffect(() => {
    const sync = () => { const p = readPreviewRegion(); if (p) setRegion(p); };
    window.addEventListener("findloop:preview-region-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("findloop:preview-region-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    api("/public/stats", { auth: false }).then(setStats).catch(() => {});
    api("/reviews", { auth: false }).then((r) => setReviews(Array.isArray(r) ? r : (r.reviews || []))).catch(() => {});
    const loadFeed = () => api("/public/home-feed", { auth: false }).then(setFeed).catch(() => {});
    loadFeed();
    // Auto-refresh the "Dernières retrouvailles" ticker + latest ads without a full reload.
    const iv = setInterval(loadFeed, 25000);
    const onVis = () => { if (document.visibilityState === "visible") loadFeed(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [lang]);

  const steps = [1, 2, 3, 4].map((n) => ({
    tag: t(`step${n}.tag`), title: t(`step${n}.title`), text: t(`step${n}.text`),
    img: [IMAGES.howPartnerScan, IMAGES.howDeclareLoss, IMAGES.howFindoRobot, IMAGES.howParcelDelivery][n - 1],
    badge: `${n === 1 ? t("hc.step") : ""}`,
  }));

  return (
    <div className="bg-background">
      {/* ---------- HERO (keys on bench + cream card) ---------- */}
      <section className="relative">
        <div className="relative h-[70vh] min-h-[540px] w-full overflow-hidden photo-scrim">
          <img src={IMAGES.keysBench} alt={t("hc.keysForgottenOnABench")} className="w-full h-full object-cover object-[center_42%]" data-testid="hero-image" />
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative -mt-24 md:-mt-28 cream rounded-[2rem] card-shadow p-8 md:p-14 max-w-3xl">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span className="inline-flex items-center gap-2 rounded-full badge-red px-3.5 py-1.5 text-xs font-bold tracking-wide uppercase">{t("hero.badge")}</span>
              <LanguageSelect testid="language-select-hero" />
            </div>
            <h1 className="mt-6 font-display text-4xl md:text-6xl font-semibold leading-[1.02] text-findo-navy">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth/register">
                <Button size="lg" className="rounded-full h-12 px-8 font-bold text-base bg-findo-navy hover:bg-findo-navy/90" data-testid="hero-cta-partner">
                  {t("hero.ctaPartner")} <ArrowRight size={18} className="ml-1.5" />
                </Button>
              </Link>
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              <Link to="/declarer">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 font-bold text-base border-findo-red text-findo-red hover:bg-findo-red/5" data-testid="hero-cta-lost">
                  <PackageSearch size={18} className="mr-1.5" /> {t("hero.ctaLost")}
                </Button>
              </Link>
              <Link to="/annonces/trouvee">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 font-bold text-base border-findo-navy text-findo-navy hover:bg-findo-navy/5" data-testid="hero-cta-found">
                  <Search size={18} className="mr-1.5" /> {t("hero.ctaFound")}
                </Button>
              </Link>
            </div>
            {stats && (
              <div className="mt-10 flex flex-wrap gap-10 border-t border-black/10 pt-6">
                <Stat n={stats.partners ?? stats.total_partners ?? 0} l={t("stat.partners")} />
                <Stat n={stats.items ?? stats.total_items ?? 0} l={t("stat.items")} />
                <Stat n={stats.restituted ?? stats.total_restituted ?? 0} l={t("stat.restituted")} />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ---------- PRESENTATION VIDEO ---------- */}
      <section id="video" className="max-w-5xl mx-auto px-6 pt-16 md:pt-20 pb-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
          data-testid="home-video-section"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-findo-red">{t("home.videoEyebrow") === "home.videoEyebrow" ? "En vidéo" : t("home.videoEyebrow")}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-findo-navy">{t("home.videoTitle") === "home.videoTitle" ? "Découvrez findloop en 2 minutes" : t("home.videoTitle")}</h2>
          <p className="mt-4 text-slate-600">{t("home.videoSubtitle") === "home.videoSubtitle" ? "Comment nous réunissons objets perdus et propriétaires, en toute simplicité." : t("home.videoSubtitle")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-8 md:mt-10"
        >
          <div className="relative w-full aspect-video overflow-hidden rounded-2xl border border-border card-shadow bg-black" data-testid="home-video-embed">
            <iframe
              className="absolute inset-0 h-full w-full"
              src={homeEmbed}
              title={t("hc.findoPresentationVideo")}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </motion.div>
      </section>


      {/* ---------- RECENT RESTITUTIONS TICKER ---------- */}
      <RestitutionsTicker items={feed?.restitutions} t={t} />

      <Dialog open={physInfo} onOpenChange={setPhysInfo}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="physical-mgmt-modal">
          <DialogHeader>
            <DialogTitle icon={PackageCheck} className="flex items-center gap-2">{t("pricing.physModalTitle") === "pricing.physModalTitle" ? "Gestion physique des objets" : t("pricing.physModalTitle")}</DialogTitle>
          </DialogHeader>
          <ul className="space-y-4 text-sm text-slate-600 mt-2">
            <li>
              <p className="font-semibold text-findo-navy">🗂️ {t("pricing.physShelfTitle") === "pricing.physShelfTitle" ? "Plan d'étagères" : t("pricing.physShelfTitle")}</p>
              <p className="mt-0.5">{t("pricing.physShelfDesc") === "pricing.physShelfDesc" ? "Chaque objet enregistré peut se voir attribuer un emplacement physique dans l'établissement." : t("pricing.physShelfDesc")}</p>
            </li>
            <li>
              <p className="font-semibold text-findo-navy">🏷️ {t("pricing.physQrTitle") === "pricing.physQrTitle" ? "Étiquette QR dédiée" : t("pricing.physQrTitle")}</p>
              <p className="mt-0.5">{t("pricing.physQrDesc") === "pricing.physQrDesc" ? "Un QR code unique est généré par objet et renvoie vers toutes ses informations." : t("pricing.physQrDesc")}</p>
            </li>
            <li>
              <p className="font-semibold text-findo-navy">🔄 {t("pricing.physInvTitle") === "pricing.physInvTitle" ? "Suivi de l'inventaire physique" : t("pricing.physInvTitle")}</p>
              <p className="mt-0.5">{t("pricing.physInvDesc") === "pricing.physInvDesc" ? "Lorsqu'un objet est restitué, son emplacement est automatiquement libéré et disponible pour un nouvel objet." : t("pricing.physInvDesc")}</p>
            </li>
          </ul>
        </DialogContent>
      </Dialog>

      {/* ---------- HOW IT WORKS ---------- */}
      <section id="how" className="max-w-6xl mx-auto px-6 pt-24 pb-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-findo-red">{t("how.eyebrow")}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-findo-navy">{t("how.title")}</h2>
        </div>
        <div className="mt-16 space-y-10">
          {steps.map((s, i) => (
            <EditorialRow key={i} reverse={i % 2 === 1} badge={`0${i + 1}`} tag={s.tag} title={s.title} text={s.text} img={s.img} imgAlt={s.title} robotGlow={i === 2} />
          ))}
        </div>
      </section>

      {/* ---------- PROMO BAND ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="relative rounded-[2rem] overflow-hidden card-shadow">
          <img src={IMAGES.promoPartners} alt="" className="w-full h-[420px] object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-t from-findo-navy/85 via-findo-navy/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 md:p-12 max-w-xl text-white">
            <span className="inline-flex items-center gap-2 rounded-full badge-red px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">{t("promo.badge")}</span>
            <h3 className="mt-5 font-display text-3xl md:text-4xl font-semibold">{t("promo.title")}</h3>
            <p className="mt-3 text-white/85">{t("promo.text")}</p>
            <Link to="/auth/register" className="inline-block mt-6">
              <Button className="rounded-full h-12 px-7 font-bold bg-white text-findo-navy hover:bg-white/90">{t("promo.cta")}</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- FEATURES ---------- */}
      <section className="cream">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
          {[1, 2, 3].map((n) => (
            <div key={n} className="animate-fade-up" style={{ animationDelay: `${n * 0.1}s` }}>
              <div className="h-12 w-12 rounded-full findo-gradient grid place-items-center text-white font-display text-xl">{n}</div>
              <h3 className="mt-5 font-display text-2xl font-semibold text-findo-navy">{t(`feat${n}.t`)}</h3>
              <p className="mt-2 text-slate-600">{t(`feat${n}.d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- CHAMPIONS SHOWCASE ---------- */}
      <ChampionsShowcase t={t} />

      {/* ---------- RECENT ADS ---------- */}
      <RecentAdsSection ads={feed?.ads} t={t} />

      {/* ---------- PRICING ---------- */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-findo-red">{t("pricing.eyebrow")}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-findo-navy">{t("pricing.title")}</h2>
          <p className="mt-4 text-slate-600">{t("pricing.subtitle")}</p>
          {stats?.app_env === "production" && (
            <span
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-findo-navy/10 bg-findo-navy/[0.04] px-3 py-1 text-xs font-semibold text-findo-navy/80"
              data-testid="pricing-einvoice-badge"
            >
              <BadgeCheck size={14} className="text-findo-red" /> {t("hero.einvoiceBadge")}
            </span>
          )}
        </div>

        {/* Billing cycle selector — annual = -20% */}
        <div className="mt-8 flex justify-center" data-testid="pricing-billing-toggle">
          <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${billing === "monthly" ? "bg-findo-navy text-white shadow" : "text-slate-500 hover:text-findo-navy"}`}
              data-testid="pricing-billing-monthly">
              {t("pricing.monthly") === "pricing.monthly" ? "Mensuel" : t("pricing.monthly")}
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${billing === "annual" ? "bg-findo-navy text-white shadow" : "text-slate-500 hover:text-findo-navy"}`}
              data-testid="pricing-billing-annual">
              {t("pricing.annual") === "pricing.annual" ? "Annuel" : t("pricing.annual")}
              <span className="inline-flex items-center gap-1 rounded-full bg-findo-red text-white px-2 py-0.5 text-[11px] font-bold">
                <Sparkles size={11} className="fill-white" /> {t("pricing.annualBadge") === "pricing.annualBadge" ? "Offre -20%" : t("pricing.annualBadge")}
              </span>
            </button>
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
          {plans.map((tier) => {
            const featured = tier.featured;
            const isAnnual = billing === "annual" && !!tier.price_eur;
            const annualEur = tier.price_eur ? Math.round(tier.price_eur * 12 * 0.8) : null;
            const fullAnnualEur = tier.price_eur ? tier.price_eur * 12 : null;
            const priceLabel = isAnnual ? formatMoneyInt(annualEur, lang) : tier.price;
            const subLabel = isAnnual ? (t("pricing.perYear") === "pricing.perYear" ? "/an" : t("pricing.perYear")) : tier.sub;
            const fcfaEur = isAnnual ? annualEur : tier.price_eur;
            return (
              <div key={tier.key} className={`rounded-[1.5rem] p-7 flex flex-col ${featured ? "bg-findo-navy text-white card-shadow" : "cream"}`} data-testid={`pricing-card-${tier.key}`}>
                {featured && <span className="self-start mb-3 inline-flex items-center gap-1 text-xs font-bold badge-red px-2.5 py-1 rounded-full"><Star size={12} className="fill-white" /> {t("pricing.popular")}</span>}
                <h3 className={`font-display text-lg font-semibold leading-snug ${featured ? "" : "text-findo-navy"}`}>{tier.name}</h3>
                <p className="mt-3">
                  <span className="text-3xl font-display font-semibold">{priceLabel}</span>
                  {tier.price_eur > 0 && <span className={`ml-1 text-xs font-semibold ${featured ? "text-white/70" : "text-slate-500"}`} data-testid={`landing-plan-ht-${tier.key}`}>{t("price.ht")}</span>}
                  {subLabel && <span className={featured ? "text-white/60 text-sm" : "text-slate-400 text-sm"}>{subLabel}</span>}
                </p>
                {isAnnual && (
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid={`pricing-annual-note-${tier.key}`}>
                    <span className={`line-through ${featured ? "text-white/50" : "text-slate-400"}`}>{formatMoneyInt(fullAnnualEur, lang)}/an</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-findo-red/10 text-findo-red px-2 py-0.5 font-bold">−20%</span>
                    <span className={`font-bold ${featured ? "text-white" : "text-findo-red"}`}>
                      {t("pricing.saveLabel") === "pricing.saveLabel" ? "Vous économisez" : t("pricing.saveLabel")} {formatMoneyInt(fullAnnualEur - annualEur, lang)}/an
                    </span>
                  </p>
                )}
                {region === "afrique" && fcfaEur && (
                  <p className={`text-xs mt-0.5 ${featured ? "text-white/60" : "text-slate-400"}`} data-testid="pricing-fcfa">
                    ≈ {eurToFcfa(fcfaEur, lang)} FCFA {t("price.ht")}{subLabel}
                  </p>
                )}
                <ul className="mt-5 space-y-2.5 flex-1">
                  {tier.features.filter((f) => !/-?\s*20\s*%/.test(f)).map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${featured ? "text-white/85" : "text-slate-600"}`}>
                      <Check size={16} className={`${featured ? "text-white" : "text-findo-red"} mt-0.5 shrink-0`} />
                      <span className="flex flex-wrap items-center gap-1.5">{f}</span>
                    </li>
                  ))}
                  <li className={`flex items-start gap-2 text-sm ${featured ? "text-white/85" : "text-slate-600"}`} data-testid={`pricing-physical-${tier.key}`}>
                    <Check size={16} className={`${featured ? "text-white" : "text-findo-red"} mt-0.5 shrink-0`} />
                    <span className="flex items-center gap-1.5">
                      📦 {t("pricing.physicalMgmt") === "pricing.physicalMgmt" ? "Gestion physique des objets (étagères, QR, inventaire)" : t("pricing.physicalMgmt")}
                      <button type="button" onClick={() => setPhysInfo(true)} data-testid={`pricing-physical-info-${tier.key}`}
                        className={`shrink-0 leading-none rounded-full ${featured ? "text-white/80 hover:text-white" : "text-findo-navy/60 hover:text-findo-navy"}`} aria-label="Info">
                        <Info size={15} />
                      </button>
                    </span>
                  </li>
                  {tier.features.filter((f) => /-?\s*20\s*%/.test(f)).map((f) => {
                    const label = f.replace(/\s*\(?-?\s*20\s*%\)?/g, "").trim();
                    return (
                      <li key={f} className={`flex items-start gap-2 text-sm ${featured ? "text-white/85" : "text-slate-600"}`}>
                        <Check size={16} className={`${featured ? "text-white" : "text-findo-red"} mt-0.5 shrink-0`} />
                        <span className="flex flex-wrap items-center gap-1.5">
                          {label}
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="discount-badge cursor-help" data-testid={`pricing-discount-badge-${tier.key}`}>
                                  <Sparkles size={11} className="fill-white" /> −20%
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[220px] text-center" data-testid={`pricing-savings-tip-${tier.key}`}>
                                {t("pricing.saveLabel")} <b>{formatMoneyInt(annualSecondarySavings(region, tier.key), lang)}</b>{t("pricing.savePerYear")}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {tier.quote ? (
                  <div className="mt-auto pt-6">
                    <Button
                      onClick={() => setQuoteSegment(tier.name)}
                      className="w-full rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 text-white"
                      data-testid="pricing-quote-btn">
                      {t("pricing.requestQuote")}
                    </Button>
                  </div>
                ) : (
                  <Link to={partner && tier.key !== "free" ? `/b2b/account?subscribe=${tier.key}` : "/auth/register"} className="mt-auto pt-6 block"
                    onClick={() => { try { localStorage.setItem("findo_plan_cycle", billing); localStorage.setItem("findo_plan_region", region); localStorage.setItem("findo_plan_tier", tier.key); } catch { /* ignore */ } }}>
                    <Button className={`w-full rounded-full font-semibold ${featured ? "bg-white text-findo-navy hover:bg-white/90" : "bg-findo-navy hover:bg-findo-navy/90"}`}>
                      {tier.key === "free" ? t("pricing.start") : t("pricing.choose")}
                    </Button>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <QuoteDialog segment={quoteSegment} region={region} onClose={() => setQuoteSegment(null)} t={t} />

      {/* ---------- PUBLIC IMPACT HEATMAP ---------- */}
      <PublicImpactMap />

      {/* ---------- REVIEWS / TESTIMONIALS ---------- */}
      <ReviewsSection reviews={reviews} t={t} />

      {/* ---------- CTA ---------- */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-[2rem] findo-gradient p-12 md:p-16 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 hero-mesh opacity-20" />
          <h2 className="relative font-display text-4xl md:text-5xl font-semibold">{t("cta.title")}</h2>
          <p className="relative mt-4 text-white/85 max-w-xl mx-auto">{t("cta.subtitle")}</p>
          <Link to="/auth/register" className="relative inline-block mt-8">
            <Button size="lg" className="rounded-full h-12 px-9 font-bold bg-white text-findo-navy hover:bg-white/90">{t("cta.button")}</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function ChampionsShowcase({ t }) {
  const [finders, setFinders] = useState([]);
  const [champion, setChampion] = useState(null);
  useEffect(() => {
    api("/public/finder-leaderboard?limit=3", { auth: false })
      .then((r) => { setFinders(r.entries || []); setChampion(r.champion || null); })
      .catch(() => {});
  }, []);
  if (finders.length === 0) return null;
  const order = finders.length >= 3 ? [finders[1], finders[0], finders[2]] : finders;
  const rings = finders.length >= 3 ? ["ring-slate-300", "ring-amber-400", "ring-amber-700"] : ["ring-amber-400"];
  const heights = finders.length >= 3 ? ["h-16", "h-24", "h-12"] : ["h-24"];
  return (
    <section className="cream" data-testid="home-champions">
      <div className="max-w-5xl mx-auto px-6 py-20 text-center">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-findo-red">
          <Trophy size={13} /> {t("nav.ranking")}
        </span>
        <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-findo-navy">{t("home.champTitle")}</h2>
        <p className="mt-4 text-slate-600 max-w-xl mx-auto">{t("home.champSubtitle")}</p>

        {champion && (
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-amber-200 bg-white px-5 py-2.5 card-shadow" data-testid="home-champion">
            <Crown size={18} className="text-amber-400" />
            <span className="text-sm font-semibold text-findo-navy">{t("lb.champion")} {champion.year} · {champion.name}</span>
          </div>
        )}

        <div className="mt-10 flex items-end justify-center gap-4 sm:gap-8">
          {order.map((r, i) => (
            <div key={r.id} className="flex flex-col items-center w-24 sm:w-32" data-testid={`home-podium-${r.rank}`}>
              <div className={`h-16 w-16 rounded-full overflow-hidden bg-white border grid place-items-center ring-2 ${rings[i] || "ring-slate-200"}`}>
                {r.avatar_base64 ? <img src={r.avatar_base64} alt="" className="h-full w-full object-cover" /> : <Trophy size={26} className="text-amber-300" />}
              </div>
              <p className="mt-2 text-sm font-semibold text-findo-navy truncate w-full">{r.name}</p>
              <p className="text-xs font-bold text-findo-red">{r.score} {t("lb.pts")}</p>
              <div className={`mt-2 w-full ${heights[i] || "h-16"} rounded-t-xl bg-gradient-to-b from-findo-navy to-[#0f1e3a] text-white grid place-items-center`}>
                <span className="font-display text-3xl font-semibold">{r.rank}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/classement">
            <Button size="lg" className="rounded-full h-12 px-8 font-bold bg-findo-navy hover:bg-findo-navy/90" data-testid="home-champ-cta">{t("home.champCta")}</Button>
          </Link>
          <Link to="/mon-compte">
            <Button size="lg" variant="outline" className="rounded-full h-12 px-8 font-bold border-findo-navy text-findo-navy hover:bg-findo-navy/5" data-testid="home-champ-become">{t("home.champBecome")}</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function QuoteDialog({ segment, region, onClose, t }) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", sites: "", message: "",
    country: "FR", kbis: "", vat_number: "", phone: "", address_street: "", address_city: "", address_postal: "", address_country: "" });
  const [busy, setBusy] = useState(false);
  const open = !!segment;
  const frRequired = form.country === "FR";
  const resetForm = () => setForm({ name: "", email: "", organization: "", sites: "", message: "",
    country: "FR", kbis: "", vat_number: "", phone: "", address_street: "", address_city: "", address_postal: "", address_country: "" });

  async function submit(e) {
    e.preventDefault();
    if (frRequired && (!form.kbis.trim() || !form.vat_number.trim())) {
      toast.error(t("hc.theKbisNumberAndVat")); return;
    }
    setBusy(true);
    try {
      await api("/public/quote-request", {
        method: "POST", auth: false,
        body: { ...form, sites: form.sites ? Number(form.sites) : null, region, segment },
      });
      toast.success(t("pricing.quoteSent"));
      onClose();
      resetForm();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle icon={Send}>{t("pricing.quoteTitle")}</DialogTitle>
          <DialogDescription>{segment} · {region === "afrique" ? t("hc.africa") : "Europe"}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="findo-numbered space-y-5">
          <div><Label>{t("mc.name")}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-name" /></div>
          <div><Label>{t("mc.email")}</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-email" /></div>
          <div><Label>{t("pricing.quoteOrg")}</Label><Input required value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-org" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("hc.country")}</Label>
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm" data-testid="quote-country">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{`${c.flag} ${c.name}`}</option>)}
              </select>
            </div>
            <div><Label>{t("pricing.quoteSites")}</Label><Input type="number" min="0" value={form.sites} onChange={(e) => setForm({ ...form, sites: e.target.value })} placeholder="Ex. 3" className="mt-1.5 h-11 rounded-xl" data-testid="quote-sites" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("hc.kbisNumber")}{frRequired ? " *" : ""}</Label><Input value={form.kbis} onChange={(e) => setForm({ ...form, kbis: e.target.value })} required={frRequired} className="mt-1.5 h-11 rounded-xl" data-testid="quote-kbis" /></div>
            <div><Label>{t("hc.vatNumber")}{frRequired ? " *" : ""}</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} required={frRequired} className="mt-1.5 h-11 rounded-xl" data-testid="quote-vat" /></div>
          </div>
          <div><Label>{t("hc.phoneOptional")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-phone" /></div>
          <div><Label>{t("hc.addressStreet")}</Label><Input value={form.address_street} onChange={(e) => setForm({ ...form, address_street: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-street" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>{t("hc.city")}</Label><Input value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-city" /></div>
            <div><Label>Code postal</Label><Input value={form.address_postal} onChange={(e) => setForm({ ...form, address_postal: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-postal" /></div>
            <div><Label>{t("hc.countryAddress")}</Label><Input value={form.address_country} onChange={(e) => setForm({ ...form, address_country: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="quote-addr-country" /></div>
          </div>
          <div><Label>{t("pricing.quoteMsg")}</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" rows={3} data-testid="quote-message" /></div>
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="quote-submit">
            <Send size={16} className="mr-1.5" /> {busy ? <FindoSpinner size={16} /> : t("pricing.quoteSend")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// Robot illustration: "fl" chest letters light up in emerald once, when the row enters the viewport.
function RobotGlowImage({ img, imgAlt }) {
  const ref = useRef(null);
  const [lit, setLit] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setLit(true); io.disconnect(); }
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className="robot-frame absolute inset-0" data-testid="robot-glow-frame" data-lit={lit ? "true" : "false"}>
      <div className="robot-cover">
        <img src={img} alt={imgAlt} className="w-full h-full" />
        <span className={`robot-fl-halo ${lit ? "is-lit" : ""}`} aria-hidden="true" />
        <img src="/images/robot-fl-letters.png" alt="" aria-hidden="true" className={`robot-fl-letters ${lit ? "is-lit" : ""}`} data-testid="robot-fl-glow" />
      </div>
    </div>
  );
}

function EditorialRow({ img, imgAlt, badge, tag, title, text, reverse, robotGlow }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
      <div className="relative rounded-[1.75rem] overflow-hidden h-72 md:h-96 card-shadow">
        {robotGlow ? <RobotGlowImage img={img} imgAlt={imgAlt} /> : <img src={img} alt={imgAlt} className="w-full h-full object-cover" />}
        <span className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full badge-red px-3 py-1 text-xs font-bold uppercase tracking-wide">{badge}</span>
      </div>
      <div>
        <span className="text-xs font-bold uppercase tracking-widest text-findo-red">{tag}</span>
        <h3 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-findo-navy leading-tight">{title}</h3>
        <p className="mt-4 text-lg text-slate-600">{text}</p>
      </div>
    </motion.div>
  );
}

function useCountUp(target, duration = 2000) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const run = () => {
      if (started.current) return;
      started.current = true;
      const start = performance.now();
      const from = 0;
      const step = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        setVal(Math.round(from + (target - from) * eased));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && run()),
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  return [val, ref];
}

function Stat({ n, l }) {
  const [val, ref] = useCountUp(Number(n) || 0);
  return (
    <div ref={ref} data-testid="home-stat">
      <p className="font-display text-3xl font-semibold text-findo-navy" data-testid="home-stat-value">{val.toLocaleString(document.documentElement.lang || "fr-FR")}</p>
      <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{l}</p>
    </div>
  );
}

function feedText(r, t) {
  if (!r.city) return r.text;
  if (r.demo) {
    const obj = t(`hc.demoObj${r.obj_idx}`);
    return t("hc.feedDemo").replace("{obj}", obj.charAt(0).toUpperCase() + obj.slice(1)).replace("{city}", r.city).replace("{when}", t(`hc.demoWhen${r.when_idx}`));
  }
  return t("hc.feedReal").replace("{cat}", labelFor(r.category)).replace("{city}", r.city);
}

function RestitutionsTicker({ items, t }) {
  if (!items || items.length === 0) return null;
  const loop = [...items, ...items];
  const TT = (k, d) => (t && t(k) !== k ? t(k) : d);
  return (
    <div className="max-w-6xl mx-auto px-6 mt-8" data-testid="home-restitutions">
      <div className="bg-findo-navy text-white overflow-hidden rounded-2xl">
        <div className="px-6 py-3 flex items-center gap-4">
          <span className="hidden sm:inline-flex items-center gap-1.5 shrink-0 text-xs font-bold uppercase tracking-widest text-amber-300">
            <Sparkles size={13} /> {TT("home.reunions", "Dernières retrouvailles")}
          </span>
          <div className="findo-marquee-mask overflow-hidden flex-1">
            <div className="flex gap-10 w-max findo-marquee-track" style={{ animationDuration: "40s" }}>
              {loop.map((r, i) => (
                <span key={i} className="inline-flex items-center gap-2 text-sm text-white/90 whitespace-nowrap" data-testid="home-restitution-item">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {feedText(r, t)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecentAdsSection({ ads, t }) {
  if (!ads || ads.length === 0) return null;
  const TT = (k, d) => (t && t(k) !== k ? t(k) : d);
  return (
    <section className="max-w-6xl mx-auto px-6 py-20" data-testid="home-recent-ads">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <span className="text-xs font-bold uppercase tracking-widest text-findo-red">{TT("home.recentAdsEyebrow", "En ce moment")}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-findo-navy">{TT("home.recentAdsTitle", "Dernières annonces")}</h2>
          <p className="mt-3 text-slate-600">{TT("home.recentAdsSubtitle", "Objets perdus et trouvés signalés par la communauté.")}</p>
        </div>
        <Link to="/annonces">
          <Button variant="outline" className="rounded-full h-11 px-6 font-semibold border-findo-navy text-findo-navy hover:bg-findo-navy/5" data-testid="home-recent-ads-all">
            {TT("home.recentAdsAll", "Voir toutes les annonces")} <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </Link>
      </div>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {ads.map((ad, i) => {
          const isFound = ad.ad_type === "found";
          const img = ad.image_base64 || ad.image_url;
          const to = ad.demo ? "/annonces" : `/annonces/${ad.id}`;
          return (
            <Link key={ad.id || i} to={to} className="group block" data-testid={`home-ad-card-${i}`}>
              <div className="rounded-[1.25rem] overflow-hidden card-shadow bg-white h-full flex flex-col">
                <div className="relative h-44 bg-slate-100 overflow-hidden">
                  {img
                    ? <img src={img} alt={ad.description || ad.category} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    : <div className="w-full h-full grid place-items-center text-slate-300"><PackageSearch size={40} /></div>}
                  <span className={`absolute top-3 left-3 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${isFound ? "bg-emerald-500 text-white" : "badge-red"}`}>
                    {isFound ? TT("an.found", "Trouvé") : TT("an.lost", "Perdu")}
                  </span>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  {ad.category && <span className="text-[11px] font-bold uppercase tracking-wide text-findo-red">{ad.category}</span>}
                  <p className="mt-1 text-sm font-semibold text-findo-navy leading-snug line-clamp-2 flex-1">{ad.description}</p>
                  {ad.place && (
                    <p className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                      <MapPin size={12} /> {ad.place}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

const REVIEW_MARQUEE_DURATION = "45s";

function ReviewsSection({ reviews, t }) {
  if (!reviews || reviews.length === 0) return null;

  // Unified list: all validated reviews (B2C + B2B), highest-rated first.
  const list = [...reviews].sort((a, b) => (b.rating || 0) - (a.rating || 0));
  const avg = reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;

  return (
    <section id="reviews" className="cream" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-findo-red">{t("rev.storiesEyebrow")}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-findo-navy" data-testid="reviews-title">{t("rev.storiesTitle")}</h2>
          <p className="mt-4 text-slate-600">{t("rev.storiesSubtitle")}</p>
          <div className="mt-5 inline-flex items-center gap-2">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={18} className={`${i <= Math.round(avg) ? "fill-findo-red text-findo-red" : "text-slate-300"}`} />
              ))}
            </div>
            <span className="text-sm font-semibold text-slate-500">{t("rev.based").replace("{n}", reviews.length)}</span>
          </div>
        </div>

        <div className="mt-12 overflow-hidden" data-testid="reviews-marquee">
          <div className="flex items-stretch gap-6 w-max findo-marquee-track py-2" style={{ animationDuration: REVIEW_MARQUEE_DURATION }}>
            {[...list, ...list].map((r, i) => (
              <div key={(r.id || i) + "-" + i} className="w-[340px] shrink-0">
                <ReviewCard review={r} t={t} animate={false} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReviewCard({ review, delay, t, animate = true }) {
  const displayName = review.first_name || (review.author_name || "?").trim().split(" ")[0];
  const initials = (review.first_name || review.author_name || "?").trim().slice(0, 2).toUpperCase();
  const photoUrl = review.photo_url || (review.has_photo ? `${API}/reviews/${review.id}/photo` : null);
  const reply = review.partner_reply;
  const readMore = t("rev.readMore") === "rev.readMore" ? "Lire la suite" : t("rev.readMore");

  const bodyRef = useRef(null);
  const replyRef = useRef(null);
  const [bodyClamped, setBodyClamped] = useState(false);
  const [replyClamped, setReplyClamped] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (bodyRef.current) setBodyClamped(bodyRef.current.scrollHeight - 1 > bodyRef.current.clientHeight);
    if (replyRef.current) setReplyClamped(replyRef.current.scrollHeight - 1 > replyRef.current.clientHeight);
  }, [review]);

  const Wrapper = animate ? motion.div : "div";
  const motionProps = animate
    ? {
        initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1], delay },
        whileHover: { y: -6, transition: { duration: 0.25, ease: "easeOut" } },
      }
    : {};
  return (
    <>
      <Wrapper
        {...motionProps}
        className="bg-white rounded-[1.5rem] card-shadow flex flex-col overflow-hidden transition-shadow duration-300 hover:shadow-2xl h-[480px]" data-testid="review-card">
        {photoUrl && (
          <div className="relative h-40 w-full shrink-0 overflow-hidden bg-slate-100">
            <img src={photoUrl} alt={review.item_name || ""} loading="lazy"
              className="h-full w-full object-cover" data-testid="review-photo"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
          </div>
        )}
        <div className="p-7 flex flex-col flex-1 min-h-0">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={15} className={`${i <= (review.rating || 0) ? "fill-findo-red text-findo-red" : "text-slate-200"}`} />
            ))}
          </div>
          {review.title && <h4 className="mt-3 font-display text-[15px] font-semibold text-findo-navy leading-snug line-clamp-1">{review.title}</h4>}
          <p ref={bodyRef} className="mt-2 text-slate-600 text-[15px] leading-relaxed line-clamp-3">{review.body}</p>
          {bodyClamped && (
            <button type="button" onClick={() => setOpen(true)} className="mt-1 self-start text-sm font-bold text-findo-red hover:underline" data-testid="review-read-more">
              {readMore}
            </button>
          )}
          <div className="mt-auto pt-4 border-t border-black/5">
            <div className="flex items-center gap-3">
              {review.avatar_url
                ? <AvatarImg src={review.avatar_url} alt={displayName} loading="lazy" className="h-10 w-10 rounded-full object-cover shrink-0" data-testid="review-avatar" />
                : <div className="h-10 w-10 rounded-full findo-gradient grid place-items-center text-white font-display text-sm font-semibold shrink-0">{initials}</div>}
              <div className="min-w-0">
                <p className="font-semibold text-findo-navy text-sm truncate" data-testid="review-author">{displayName}</p>
                {review.org_name ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500" data-testid="review-restituted-by">
                    <Store size={12} /> {t("rev.restitutedBy").replace("{org}", review.org_name)}
                  </span>
                ) : review.verified && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                    <BadgeCheck size={13} /> {t("rev.verified")}
                  </span>
                )}
              </div>
            </div>
            {reply && (
              <div className="mt-3 rounded-xl bg-findo-navy/[0.04] border-l-4 border-findo-navy p-2.5" data-testid="review-partner-reply">
                <p className="text-[11px] font-bold text-findo-navy truncate">↳ {reply.author_name}</p>
                <p ref={replyRef} className="mt-0.5 text-sm text-slate-600 leading-snug line-clamp-2">{reply.body}</p>
                {replyClamped && (
                  <button type="button" onClick={() => setOpen(true)} className="mt-1 text-xs font-bold text-findo-red hover:underline" data-testid="review-reply-read-more">
                    {readMore}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </Wrapper>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto" data-testid="review-modal">
          <DialogHeader>
            <div className="flex items-center gap-1 mb-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={16} className={`${i <= (review.rating || 0) ? "fill-findo-red text-findo-red" : "text-slate-200"}`} />
              ))}
            </div>
            <DialogTitle className="font-display text-findo-navy">{review.title || (t("rev.fullReview") === "rev.fullReview" ? "Avis complet" : t("rev.fullReview"))}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-700 text-[15px] leading-relaxed whitespace-pre-line">{review.body}</p>
          <div className="mt-4 flex items-center gap-3 pt-4 border-t border-black/5">
            <div className="h-10 w-10 rounded-full findo-gradient grid place-items-center text-white font-display text-sm font-semibold shrink-0">{initials}</div>
            <div className="min-w-0">
              <p className="font-semibold text-findo-navy text-sm">{displayName}</p>
              {review.org_name ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500">
                  <Store size={12} /> {t("rev.restitutedBy").replace("{org}", review.org_name)}
                </span>
              ) : review.verified && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                  <BadgeCheck size={13} /> {t("rev.verified")}
                </span>
              )}
            </div>
          </div>
          {reply && (
            <div className="mt-4 rounded-xl bg-findo-navy/[0.04] border-l-4 border-findo-navy p-4" data-testid="review-modal-reply">
              <p className="text-xs font-bold text-findo-navy">↳ {reply.author_name}</p>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{reply.body}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
